import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  CheckCircle2,
  LockKeyhole,
  ShieldCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { authApi } from "../../backend/api/auth";
import { clearSignedOutUser, useCurrentUser } from "../../backend/queries/auth";
import { Button } from "../../components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { AppShell } from "../../components/shell";
import { Page, PageHeader } from "../../components/patterns";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { AuthLayout } from "../login/auth-layout";

const SetNewPassword = (): JSX.Element => {
  const [password, setPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token");
  const queryClient = useQueryClient();
  const {
    data: currentUser,
    error: currentUserError,
    isPending: currentUserPending,
  } = useCurrentUser({ enabled: !token });

  const initialSetup = !token && currentUser?.profile?.password_setup_required === true;

  async function signOut() {
    setSubmitting(true);
    try {
      const result = await authApi.signOut();
      if (result.error) throw result.error;
      await clearSignedOutUser(queryClient);
      navigate("/login", { replace: true });
    } catch (signOutError) {
      setError(signOutError.message || "Could not sign out");
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(() => {
    if (token) {
      setReady(true);
    } else if (currentUser) {
      setReady(true);
    } else if (!currentUserPending && currentUserError) {
      setError(currentUserError.message || "Session invalid or expired.");
    }
  }, [currentUser, currentUserError, currentUserPending, token]);

  const handleResetPassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 12) {
      setError("Password must be at least 12 characters");
      return;
    }

    setSubmitting(true);
    try {
      const { error: updateError } = token
        ? await authApi.completePassword(token, password)
        : initialSetup ? await authApi.initialPassword(password) : await authApi.changePassword(currentPassword, password);
      if (updateError) throw updateError;

      await clearSignedOutUser(queryClient);
      setSuccess("Password reset successful.");
      setTimeout(() => navigate("/login"), 800);
    } catch (updateError) {
      setError(updateError.message || "Failed to update password");
    } finally {
      setSubmitting(false);
    }
  };

  const passwordForm = (
    <Card>
      <CardHeader className="space-y-1.5 pb-5">
        <CardTitle className="flex items-center gap-2 text-lg">
          <ShieldCheck className="h-5 w-5" aria-hidden="true" />
          {token || initialSetup ? "Finish account setup" : "Change password"}
        </CardTitle>
        <CardDescription>
          {initialSetup ? "Choose your own password before entering the portal. Use at least 12 characters. Your temporary password will stop working." : token
            ? "Your email link verifies your address. Choose a password with at least 12 characters."
            : "Use at least 12 characters. A longer passphrase is easier to remember and harder to guess."}
        </CardDescription>
      </CardHeader>

      <CardContent>
        {(
          <>
            {!ready ? (
              <div
                role="status"
                className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground"
              >
                Verifying link...
              </div>
            ) : (
              <form className="space-y-5" onSubmit={handleResetPassword}>
                {!token && !initialSetup && (
                  <div className="space-y-2">
                    <Label htmlFor="current-password">Current password</Label>
                    <Input
                      id="current-password"
                      type="password"
                      value={currentPassword}
                      onChange={(event) =>
                        setCurrentPassword(event.target.value)
                      }
                      autoComplete="current-password"
                      required
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="password">New password</Label>
                  <div className="relative">
                    <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      className="!pl-12"
                      autoComplete="new-password"
                      minLength={12}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm new password</Label>
                  <div className="relative">
                    <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(event) =>
                        setConfirmPassword(event.target.value)
                      }
                      className="!pl-12"
                      autoComplete="new-password"
                      minLength={12}
                      required
                    />
                  </div>
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertTitle>Password not changed</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                {success && (
                  <Alert className="border-tone-success-border bg-tone-success-surface text-tone-success-foreground">
                    <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                    <AlertTitle>Password updated</AlertTitle>
                    <AlertDescription>{success}</AlertDescription>
                  </Alert>
                )}

                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting
                    ? "Saving..."
                    : token || initialSetup
                      ? "Set password"
                      : "Update password"}
                </Button>
                {!initialSetup && <Button
                  type="button"
                  variant="link"
                  className="w-full"
                  onClick={() => navigate(-1)}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>}
                {initialSetup && <Button type="button" variant="link" className="w-full" disabled={submitting} onClick={signOut}>Sign out</Button>}
              </form>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );

  if (!token && !initialSetup) {
    return (
      <AppShell>
        <Page width="narrow">
          <PageHeader
            title="Security"
            description="Update the password you use to sign in to the portal."
          />
          {passwordForm}
        </Page>
      </AppShell>
    );
  }

  return (
    <AuthLayout>
        {passwordForm}
    </AuthLayout>
  );
};

export default SetNewPassword;
